import React, { useRef } from 'react';
import { ArrowLeft, Calendar, Star, Trash2, MessageSquare, Share, FileText, ArrowDown } from 'lucide-react';
import { formatDate } from '../utils/dateFormatter';
import MessageItem from './MessageItem';
import EmptyState from './EmptyState';
import LoadingIndicator from './LoadingIndicator';

/**
 * Component that displays the list of messages in a conversation
 * with manual scrolling capability and no auto-scrolling
 */
const MessageList = ({
  conversation,
  messages = [],
  loading = false,
  onBack,
  onDelete,
  onShare,
  onToggleFavorite,
  isFavorite = false,
  isMobile = false
}) => {
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Manual scroll to bottom function - only used when button is clicked
  const handleScrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-800">
        <EmptyState 
          icon={MessageSquare}
          title="No conversation selected"
          description="Select a conversation from the list to view messages"
        />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-slate-800 h-full overflow-hidden">
      {/* Header with conversation info */}
      <div className="p-3 border-b border-slate-700/50 bg-slate-900/50 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-2">
          {isMobile && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-white">
                {conversation.metadata?.title || 'Untitled Chat'}
              </h3>
              {isFavorite && (
                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
              <Calendar className="w-3 h-3 text-emerald-400" />
              {formatDate(conversation.metadata?.createdAt)}
              <span className="bg-slate-700 rounded-md px-2 py-0.5 text-xs text-emerald-300">
                {conversation.metadata?.messageCount || 0} messages
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button 
            onClick={() => onShare(conversation)}
            className="p-2 hover:bg-slate-700 rounded-md text-slate-300 hover:text-emerald-300 transition-colors"
            title="Export conversation"
          >
            <FileText className="w-4 h-4" />
          </button>
          <button 
            onClick={() => onToggleFavorite(conversation)}
            className={`p-2 rounded-md transition-colors ${
              isFavorite 
                ? 'text-yellow-400'
                : 'text-slate-300 hover:text-yellow-400 hover:bg-slate-700'
            }`}
            title={isFavorite ? "Remove from favorites" : "Add to favorites"}
          >
            <Star className={`w-4 h-4 ${isFavorite ? 'fill-yellow-400' : ''}`} />
          </button>
          <button 
            onClick={() => onDelete(conversation)}
            className="p-2 hover:bg-red-500/10 rounded-md text-slate-300 hover:text-red-400 transition-colors"
            title="Delete conversation"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Messages content - fixed for proper scrolling */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto p-4 h-full max-h-[calc(100vh-180px)] scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent relative"
        style={{ 
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {loading ? (
          <LoadingIndicator />
        ) : messages.length === 0 ? (
          <EmptyState 
            icon={MessageSquare}
            title="No messages"
            description="This conversation doesn't have any messages yet."
          />
        ) : (
          <>
            {messages.map((message, index) => (
              <MessageItem key={message.id || index} message={message} />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
        
        {/* Optional scroll to bottom button - can be shown conditionally */}
        {messages.length > 5 && (
          <button
            onClick={handleScrollToBottom}
            className="absolute bottom-4 right-4 p-2 bg-slate-700 hover:bg-slate-600 rounded-full shadow-md text-white transition-colors"
            title="Scroll to bottom"
          >
            <ArrowDown className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default React.memo(MessageList);